import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
const ACTIVE_KEY = "bridge_active_participant"; // non-sensitive UI state only

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [activeParticipant, setActiveParticipantState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(ACTIVE_KEY)) || null; }
    catch (e) { return null; }
  });

  const setActiveParticipant = useCallback((p) => {
    setActiveParticipantState(p);
    try {
      if (p) localStorage.setItem(ACTIVE_KEY, JSON.stringify(p));
      else localStorage.removeItem(ACTIVE_KEY);
    } catch (e) {
      console.error("Failed to persist active participant:", e);
    }
  }, []);

  // On mount, ask the backend who we are using the httpOnly cookie.
  useEffect(() => {
    let active = true;
    api.get("/auth/me")
      .then((res) => { if (active) setUser(res.data); })
      .catch(() => { if (active) setUser(false); });
    return () => { active = false; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (e) { console.error("Logout request failed:", e); }
    setUser(false);
    setActiveParticipant(null);
  }, [setActiveParticipant]);

  const refreshUser = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); }
    catch (e) { console.error("Failed to refresh user:", e); }
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, register, logout, refreshUser, activeParticipant, setActiveParticipant }),
    [user, login, register, logout, refreshUser, activeParticipant, setActiveParticipant]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
